import React, { useEffect, useState } from 'react';
import { Box, Link as MuiLink, Typography } from '@mui/material';
import { Link, useNavigate, useParams } from 'react-router-dom';
import BlogSeo from './BlogSeo';
import BlogNotFound from './BlogNotFound';
import BlogArticleCta from './BlogArticleCta';
import BlogArticleContact from './BlogArticleContact';
import BlogPostMeta from './BlogPostMeta';
import BlogPostRelated from './BlogPostRelated';
import { getBlogPostByLegacyId, getBlogPostBySlug, getSortedBlogPosts } from '../blogs/blogConfig';
import { articlePresentation, relatedHtml, imageSrcSet, bannerSizes } from '../utils/blogPresentation';
import blogSeoMetadata from '../blogs/blogSeoMetadata.json';
import { SAFEVAULT_BRAND } from '../theme/safevaultBrand';
import { normalizeArticleHtml } from '../utils/normalizeArticleHtml';
import { getSafevaultSiteUrls } from '../utils/safevaultSiteUrls';

const extractHtmlContent = (html) => {
    const publicRootMatch = html.match(/<div[^>]*id=["']public-root["'][^>]*>([\s\S]*?)<\/div>/i);
    if (publicRootMatch) {
        return publicRootMatch[1];
    }

    const bodyMatch = html.match(/<body[^>]*>([\s\S]*?)<\/body>/i);
    if (bodyMatch) {
        return bodyMatch[1];
    }

    return html;
};

const getPrerenderedArticleContent = () => {
    if (typeof window === 'undefined') return '';
    return window.__SAFEVAULT_PRERENDERED_ARTICLE_HTML__ || '';
};

const BlogDetail = () => {
    const { slug } = useParams();
    const navigate = useNavigate();
    const legacyId = /^\d+$/.test(slug || '') ? parseInt(slug, 10) : null;
    const blog = legacyId ? getBlogPostByLegacyId(legacyId) : getBlogPostBySlug(slug);
    // Keep the already-painted prerendered article in the first client render.
    // This reserves the exact article height while the source fragment is fetched.
    const [content, setContent] = useState(getPrerenderedArticleContent);

    useEffect(() => {
        window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
        document.documentElement.scrollTop = 0;
        document.body.scrollTop = 0;
    }, [slug]);

    useEffect(() => {
        if (legacyId && blog) {
            navigate(`/blog/${blog.slug}/`, { replace: true });
        }
    }, [legacyId, blog, navigate]);

    useEffect(() => {
        if (!blog) {
            setContent('');
            return;
        }

        fetch(`${process.env.PUBLIC_URL}${blog.contentPath}`)
            .then((response) => {
                if (!response.ok) {
                    throw new Error(`Failed to load content: ${response.status}`);
                }
                return response.text();
            })
            .then((data) => {
                const siteUrls = getSafevaultSiteUrls();
                setContent(
                    normalizeArticleHtml(extractHtmlContent(data), siteUrls, {
                        stripTitle: blog.title,
                        stripLeadingImages: blog.contentPath.startsWith('/blogs/') ? 1 : 0,
                        publicUrlPrefix: process.env.PUBLIC_URL || '/blog',
                    }),
                );
            })
            .catch((error) => {
                console.error('Error fetching blog content:', error);
                setContent('');
            });
    }, [blog]);

    if (!blog) {
        return (
            <>
                <BlogSeo
                    title="Page not found | SafeVault Blogs"
                    description="The requested blog article could not be found."
                    canonicalPath="/blog/404"
                    noIndex
                />
                <BlogNotFound />
            </>
        );
    }

    const isFragmentContent = blog.contentPath.startsWith('/content/');
    const seoMetadata = blogSeoMetadata[blog.slug];
    const canonicalPath = `${(blog.canonicalPath || `/blog/${blog.slug}`).replace(/\/+$/, '')}/`;
    const heroImage = /^https?:\/\//i.test(blog.image)
        ? blog.image
        : `${process.env.PUBLIC_URL}${blog.image}`;
    const heroWebp = imageSrcSet(blog);
    const presentation = articlePresentation(content);

    return (
        <>
            <BlogSeo
                title={blog.seoTitle || `${blog.title} | SafeVault Blogs`}
                description={seoMetadata.description}
                socialDescription={seoMetadata.description}
                canonicalPath={canonicalPath}
                image={blog.image}
            />
            <main className="sv-blog-post">
              <section className="sv-blog-hero">
                <Box component="nav" aria-label="Breadcrumb" sx={{ mb: 2 }}>
                    <Box component="ol" sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, listStyle: 'none', m: 0, p: 0, color: SAFEVAULT_BRAND.textMuted }}>
                        <Box component="li"><MuiLink href="/" underline="hover">Home</MuiLink></Box>
                        <Box component="li" aria-hidden="true">/</Box>
                        <Box component="li"><MuiLink component={Link} to="/blog/" underline="hover">Blog</MuiLink></Box>
                        <Box component="li" aria-hidden="true">/</Box>
                        <Box component="li" aria-current="page">{blog.title}</Box>
                    </Box>
                </Box>

                {isFragmentContent && (
                    <Box>
                        <p className="blog-static-category">{blog.category || 'Blog'}</p>
                        <Typography
                            variant="h3"
                            component="h1"
                            className="blog-detail-title"
                            sx={{
                                mt: 3,
                                mb: 1,
                                color: 'white',
                                fontFamily: 'Inter, sans-serif',
                                fontWeight: 400,
                            }}
                        >
                            {blog.title}
                        </Typography>
                        <BlogPostMeta
                            publishedDate={seoMetadata.publishedDate}
                            updatedDate={seoMetadata.updatedDate}
                        />
                    </Box>
                )}
              </section>
                        <Box className="sv-blog-banner" component="picture">
                            {heroWebp && <source srcSet={heroWebp} sizes={bannerSizes} type="image/webp" />}
                            <img
                                className="blog-hero-image"
                                src={heroImage}
                                width="1200"
                                height="630"
                                alt={blog.imageAlt || blog.title}
                                loading="eager"
                                decoding="async"
                                {...{ fetchpriority: 'high' }}
                            />
                        </Box>
              <div className="sv-blog-body-layout">
                <aside><div dangerouslySetInnerHTML={{__html:presentation.toc}} /><BlogPostRelated slug={blog.slug} /></aside>
                <article>
                <Box
                    className="blog-article-content blog-detail-body"
                    dangerouslySetInnerHTML={{ __html: presentation.html }}
                />

                <Box sx={{ maxWidth: '980px', mx: 'auto' }}>
                    <BlogArticleContact />
                    <BlogArticleCta variant="safevault" />
                </Box>
                </article>
              </div>
              <div dangerouslySetInnerHTML={{__html:relatedHtml(blog,getSortedBlogPosts(),blogSeoMetadata)}} />
            </main>
        </>
    );
};

export default BlogDetail;
